import type { Clock, FileExporter, IdGenerator, Storage } from '@anytracker/core'
import { RouterProvider, createBrowserRouter, createMemoryRouter } from 'react-router-dom'

import { ToastProvider } from './components/Toast.js'
import { InstallPrompt } from './pwa/InstallPrompt.js'
import { ServiceWorkerProvider } from './pwa/ServiceWorkerProvider.js'
import { UnsavedWorkProvider } from './pwa/UnsavedWork.js'
import { UpdatePrompt } from './pwa/UpdatePrompt.js'
import { RangeProvider } from './range/RangeProvider.js'
import { ServicesProvider } from './services/ServicesProvider.js'
import { routes } from './shell/routes.js'

interface AppProps {
  readonly storage: Storage
  readonly clock: Clock
  readonly ids: IdGenerator
  /**
   * Required, unlike on `ServicesProvider`.
   *
   * The provider defaults to an exporter that refuses, so the eleven test files
   * that never export anything do not have to construct one. Production must
   * not be able to take that path, and requiring it here is what makes
   * forgetting it a compile error in `main.tsx` rather than a backup button
   * that quietly does nothing.
   */
  readonly exporter: FileExporter
  /**
   * Where to start, for tests.
   *
   * Supplying this switches to a memory router, which is what lets a test
   * render any screen without a browser history. Production passes nothing and
   * gets real URLs.
   */
  readonly initialEntries?: readonly string[]
}

/**
 * Composition root.
 *
 * The adapters arrive as props rather than being constructed here. That is what
 * makes the whole app renderable against `InMemoryStorage` in a test, and it is
 * the same seam the native app will use to supply SQLite (ADR-0002).
 */
export function App({ storage, clock, ids, exporter, initialEntries }: AppProps) {
  const router = initialEntries
    ? createMemoryRouter(routes, { initialEntries: [...initialEntries] })
    : createBrowserRouter(routes)

  return (
    <ServicesProvider storage={storage} clock={clock} ids={ids} exporter={exporter}>
      {/*
        Outside the router, so a prompt is never unmounted by navigation. An
        update offer that vanishes because the user tapped a nav item — and
        does not come back until the next launch — is worse than no offer.
      */}
      <ServiceWorkerProvider>
        <UnsavedWorkProvider>
          <ToastProvider>
            {/*
              Outside the router too, so the range survives navigation. Moving
              from the timeline to a chart and finding a different fortnight is
              how two views of the same data end up disagreeing (T-402).
            */}
            <RangeProvider>
              <RouterProvider router={router} />
            </RangeProvider>
            <UpdatePrompt />
            <InstallPrompt />
          </ToastProvider>
        </UnsavedWorkProvider>
      </ServiceWorkerProvider>
    </ServicesProvider>
  )
}
